import {
  BaseQueryApi,
  BaseQueryFn,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryExtraOptions,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { clearAuthState } from 'features/auth/authLocalStorage';
import { authSlice, AuthState } from 'features/auth/authSlice';
// The following imports are for JS docs use only.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { customStatusCodes } from './customStatusCodes';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { customBaseQuery } from '../customBaseQuery';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type TokenLayout from 'common/components/HolyGrailLayout/TokenLayout';

// The following types are needed simply to extract the type of the parameter
// required by the Redux `forceRefetch` function, to avoid replicating it here.
type DummyQueryOptions = QueryExtraOptions<
  string,
  unknown,
  unknown,
  BaseQueryFn
>;
type ForceRefetchFunction = NonNullable<DummyQueryOptions['forceRefetch']>;
type ForceRefetchParams = Parameters<ForceRefetchFunction>[0];

/**
 * A helper function to determine whether an endpoint should be refetched
 * after an expired session was registered.
 *
 * @remark This is one part of the logic to work around a client-side cache bug
 * outlined in the {@link handleExpiredSession} function.
 */
export const shouldForceRefetch = ({
  state,
  endpointState,
}: ForceRefetchParams): boolean => {
  const { auth } = state as unknown as { auth: AuthState };

  return endpointState?.endpointName
    ? auth.endpointsToRefetch.includes(endpointState?.endpointName)
    : false;
};

/**
 * A helper function that holds the first part of a 'hacky' patch to circumvent
 * a client-side cache bug. This function deals exclusively with a 'session
 * expired' scenario, when the client receives a
 * {@link customStatusCodes.SESSION_EXPIRED}.
 *
 * @remarks
 *
 * - **The bug**: when the client registers an error from the server and then
 * navigates away (i.e. like in a 'session expired' scenario), component logic
 * that handles the error is somehow cached and re-triggered the next time the
 * client renders the component, despite a new '200' response for the same call!
 *
 *-----

 * - **The 'hacky' work-around**: (note that this solution only addresses the
 * 'session expired' scenario).
 *   1. The client detects the error, sets a 'session expired' flag, adds the
 * endpoint to an array tracking endpoints to refetch and logs the user out in
 * state and local storage.
 *   2. The error response is then scrubbed to prevent the future cached error
 * handling code from running.
 *   3. The component handling authed layout notices the user is logged out
 * and redirects the user to the login page while diplaying a session expired
 * message.
 *   4. The next time the user logs in and the client renders the component that
 * triggered the error, the client refers to the stored array of endpoints and
 * force-refetches if the endpoint is listed.
 *   5. If the post-login api call is successful, the endpoint flag is removed
 * from the array. Finally, if the last endpoint flag is removed, the 'session
 * expired' flag is turned off.
 *
 * - See {@link customBaseQuery} for more details on api handling.
 * - See {@link TokenLayout} for user re-direct and session expired banner.
 * - See {@link authSlice} for session expired flag tracking logic.
 * - See {@link shouldForceRefetch} for force re-fetch logic.
 */
export const handleExpiredSession = (
  api: BaseQueryApi,
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
): void => {
  const { dispatch, endpoint } = api;
  const { meta } = result;
  const { setSessionExpired, addEndpointToRefetch, userLoggedOut } =
    authSlice.actions;

  dispatch(setSessionExpired(true));
  dispatch(addEndpointToRefetch(endpoint));
  dispatch(userLoggedOut());
  clearAuthState();

  if (meta?.response) {
    // Scrub error to prevent having error logic cached.
    meta.response = { ...meta.response, ok: true };
    result.error = undefined;
  }
};
